import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceMetrics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('today');

  const performanceData = {
    today: {
      totalCalls: 23,
      answeredCalls: 21,
      missedCalls: 2,
      averageCallDuration: "06:45",
      totalTalkTime: "2:23:15",
      callsPerHour: 3.2,
      customerSatisfaction: 4.6,
      firstCallResolution: 85
    },
    week: {
      totalCalls: 156,
      answeredCalls: 148,
      missedCalls: 8,
      averageCallDuration: "07:12",
      totalTalkTime: "17:52:30",
      callsPerHour: 3.1,
      customerSatisfaction: 4.5,
      firstCallResolution: 82
    },
    month: {
      totalCalls: 687,
      answeredCalls: 651,
      missedCalls: 36,
      averageCallDuration: "06:58",
      totalTalkTime: "75:45:22",
      callsPerHour: 3.3,
      customerSatisfaction: 4.4,
      firstCallResolution: 79
    }
  };

  const periods = [
    { value: 'today', label: 'Bugün' },
    { value: 'week', label: 'Bu Hafta' },
    { value: 'month', label: 'Bu Ay' }
  ];

  const currentData = performanceData?.[selectedPeriod];
  const answerRate = ((currentData?.answeredCalls / currentData?.totalCalls) * 100)?.toFixed(1);

  const getMetricColor = (value, type) => {
    switch (type) {
      case 'answerRate':
        return value >= 95 ? 'text-green-600' : value >= 85 ? 'text-yellow-600' : 'text-red-600';
      case 'satisfaction':
        return value >= 4.5 ? 'text-green-600' : value >= 4.0 ? 'text-yellow-600' : 'text-red-600';
      case 'resolution':
        return value >= 80 ? 'text-green-600' : value >= 70 ? 'text-yellow-600' : 'text-red-600';
      default:
        return 'text-slate-900';
    }
  };

  const getMetricIcon = (type) => {
    switch (type) {
      case 'calls':
        return 'Phone';
      case 'duration':
        return 'Clock';
      case 'satisfaction':
        return 'Star';
      case 'resolution':
        return 'CheckCircle';
      default:
        return 'BarChart3';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Performans Metrikleri</h3>
          <p className="text-sm text-slate-600">Arama performansınızı takip edin</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
        >
          Rapor Al
        </Button>
      </div>
      {/* Period Selector */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
          {periods?.map((period) => (
            <button
              key={period?.value}
              onClick={() => setSelectedPeriod(period?.value)}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                selectedPeriod === period?.value
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {period?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Total Calls */}
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name={getMetricIcon('calls')} size={16} className="text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Toplam Arama</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {currentData?.totalCalls}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Cevaplanmış: {currentData?.answeredCalls} | Kaçırılan: {currentData?.missedCalls}
          </div>
        </div>

        {/* Answer Rate */}
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Target" size={16} className="text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Cevaplama Oranı</span>
          </div>
          <div className={`text-2xl font-bold ${getMetricColor(answerRate, 'answerRate')}`}>
            %{answerRate}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {currentData?.answeredCalls}/{currentData?.totalCalls} arama
          </div>
        </div>

        {/* Average Duration */}
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name={getMetricIcon('duration')} size={16} className="text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Ortalama Süre</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {currentData?.averageCallDuration}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Toplam: {currentData?.totalTalkTime}
          </div>
        </div>

        {/* Calls Per Hour */}
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Zap" size={16} className="text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Saatlik Arama</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {currentData?.callsPerHour}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Arama/saat ortalaması
          </div>
        </div>
      </div>
      {/* Quality Metrics */}
      <div className="space-y-4">
        <h4 className="font-semibold text-slate-900">Kalite Metrikleri</h4>
        
        {/* Customer Satisfaction */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <Icon name={getMetricIcon('satisfaction')} size={20} className="text-slate-500" />
            <div>
              <span className="font-medium text-slate-900">Müşteri Memnuniyeti</span>
              <div className="text-xs text-slate-500">5 üzerinden değerlendirme</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-xl font-bold ${getMetricColor(currentData?.customerSatisfaction, 'satisfaction')}`}>
              {currentData?.customerSatisfaction}
            </div>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <Icon
                  key={star}
                  name="Star"
                  size={12}
                  className={star <= Math.floor(currentData?.customerSatisfaction) ? 'text-yellow-400' : 'text-slate-300'}
                />
              ))}
            </div>
          </div>
        </div>

        {/* First Call Resolution */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <Icon name={getMetricIcon('resolution')} size={20} className="text-slate-500" />
            <div>
              <span className="font-medium text-slate-900">İlk Aramada Çözüm</span>
              <div className="text-xs text-slate-500">Tekrar arama gerektirmeyen çözümler</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-xl font-bold ${getMetricColor(currentData?.firstCallResolution, 'resolution')}`}>
              %{currentData?.firstCallResolution}
            </div>
            <div className="text-xs text-slate-500">
              {Math.round((currentData?.firstCallResolution / 100) * currentData?.answeredCalls)} arama
            </div>
          </div>
        </div>
      </div>
      {/* Performance Indicators */}
      <div className="mt-6 pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">Genel Performans</span>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full" />
            <span className="font-medium text-slate-900">Mükemmel</span>
          </div>
        </div>
        <div className="mt-2 w-full bg-slate-200 rounded-full h-2">
          <div className="bg-success h-2 rounded-full" style={{ width: '92%' }} />
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;